<template>
  <div>
    <data-table :title="title"
                :service="OrderService"
                :entity="entity"
                :columns="columns"
                :filters="filters"
                :operations="operations"
                :go-to-details-on-row-click="false"
                :default-sort="{prop: 'created_at', order: 'descending'}"
                @on-row-click="onRowClick"
                filterable
                ref="table">
      <template v-slot:extra-filter-buttons="{lastQuery}">
        <base-dropdown position="right">
          <base-button slot="title"
                       icon
                       outline
                       type="primary">
            <i class="nc-icon nc-settings-gear-65"></i>
          </base-button>
          <h6 class="dropdown-header">{{$t('generic.settings')}}</h6>
          <div class="dropdown-item" @click="exportOrders(lastQuery)">
            {{$t('generic.exportToCSV')}}
          </div>
          <div class="dropdown-item" @click="exportOrders(lastQuery, true)">
            {{$t('generic.exportToCSVAll')}}
          </div>
          <div class="dropdown-item" @click="exportOrdersXLSX(lastQuery)">
            {{$t('generic.exportToXLSX')}}
          </div>
          <div class="dropdown-item" @click="exportOrdersXLSX(lastQuery, true)">
            {{$t('generic.exportToXLSXAll')}}
          </div>

        </base-dropdown>
      </template>

      <template v-slot:created_at="{row}">
        {{$formatDate(row.created_at, FRIENDLY_DATETIME_FORMAT)}}
      </template>
      <template v-slot:invoice.name="{row}">
        {{getInvoiceName(row)}}
      </template>
      <template v-slot:name="{row}">
        {{row.name}}
      </template>
      <template v-slot:paymentType="{row}">
        {{getPaymentType(row.payment_type_id).name}}
      </template>
      <template v-slot:product="{row}">
        {{get(row, 'product.name', '')}}
      </template>
      <template v-slot:invoice="{row}">
        {{getInvoiceName(row)}}
      </template>
      <template v-slot:price="{row}">
        <div class="d-flex flex-column">
          <div>{{row.price | toCurrency}}</div>
          <small>{{row.credits ? `${row.credits.amount} credits` : $t('generic.na')}}</small>
        </div>
      </template>
      <template v-slot:status="{row}">
        <status-badge :status="row.status"/>
      </template>

      <template v-slot:client.name="{row}">
        <router-link @click.native.stop="" :to="`/contacts/clients/${get(row, 'client.uuid')}`">
          {{get(row, 'client.name')}}
        </router-link>
      </template>
      <template v-slot:user.name="{row}">
        <router-link @click.native.stop="" :to="`/contacts/users/${get(row, 'user.uuid')}`">
          {{get(row, 'user.given_name')}}
        </router-link>
      </template>
      <template v-slot:related="{row}">
        <div class="order__detail-cell">
          <i class="nc-icon nc-tag-content"></i> &nbsp;
          <router-link :to="getRelatedLink(row.related)" @click.stop.prevent="navigateToRelated(row.related)">
            {{get(row, 'related.data.title', $t('generic.na'))}}
          </router-link>
        </div>
      </template>

      <template v-slot:extra-buttons="{row}">
        <template v-if="row.status === orderStatuses.CHECK">
          <tooltip-icon-button type="success"
                               :content="$t('generic.approve')"
                               icon="nc-icon nc-check-2"
                               @click.stop="approveOrder(row)">
          </tooltip-icon-button>
          <tooltip-icon-button type="danger"
                               :content="$t('generic.reject')"
                               icon="nc-icon nc-simple-remove"
                               @click.stop="rejectOrder(row)">
          </tooltip-icon-button>
        </template>
      </template>
    </data-table>
  </div>
</template>
<script>
  import { mapGetters } from 'vuex'
  import get from 'lodash/get'
  import OrderService from 'src/services/OrderService'
  import TooltipIconButton from 'src/components/Buttons/TooltipIconButton'
  import entities from 'src/config/entities'
  import Badge from 'src/components/Badge'
  import { orderStatuses } from 'src/config/orderStatuses'
  import { FRIENDLY_DATETIME_FORMAT } from 'src/config/dateUtils'
  import cloneDeep from 'lodash/cloneDeep'
  import { getRouteName, routeTypes } from 'src/config/routeTypes'

  export default {
    name: 'order-list',
    components: {
      Badge,
      TooltipIconButton
    },
    props: {
      userId: {
        type: String
      },
      tableColumns: {
        type: Array
      },
      title: String,
      operations: {
        type: String,
        default: 'refresh,edit,delete'
      },
      filters: {
        type: Array,
        default: () => [
          {
            key: 'with',
            value: 'client,user,invoice,product'
          },
          {
            key: 'sort',
            value: '-created_at'
          }
        ]
      }
    },
    data () {
      return {
        OrderService,
        entity: entities.ORDER,
        exceptionColumns: ['related', 'user', 'client'],
        orderStatuses,
        FRIENDLY_DATETIME_FORMAT,
      }
    },
    computed: {
      ...mapGetters('config', ['getPaymentType']),
      columns () {
        return this.tableColumns || [
          {
            prop: 'created_at',
            label: this.$t('order.list.labels.date'),
            minWidth: '175px',
            sortable: true
          },
          {
            prop: 'client.name',
            label: this.$t('generic.client'),
            minWidth: '200px',
            sortable: true
          },
          {
            prop: 'user.name',
            label: this.$t('generic.user'),
            minWidth: '160px',
            sortable: true
          },
          {
            prop: 'status',
            label: this.$t('job.list.columns.status'),
            minWidth: '90px'
          },
          {
            prop: 'price',
            label: this.$t('order.list.labels.price'),
            minWidth: '90px',
            sortable: true
          },
          {
            prop: 'product',
            label: this.$t('order.list.labels.product'),
            minWidth: '170px'
          },
          {
            prop: 'paymentType',
            label: this.$t('order.list.labels.paymentType'),
            minWidth: '150px',
            sortable: true
          },
          {
            prop: 'invoice.name',
            label: this.$t('order.list.labels.invoice'),
            minWidth: '120px'
          },
          {
            prop: 'related',
            label: this.$t('order.list.labels.related'),
            minWidth: '180px'
          }
        ]
      }
    },
    methods: {
      get,
      onRowClick (row, event, column) {
        if (this.exceptionColumns.includes(column.property)) {
          return
        }
        this.$router.push({
          name: getRouteName(routeTypes.DETAILS, entities.ORDER),
          params: { id: row.uuid }
        })
      },
      navigateToRelated (related) {
        this.$router.push(this.getRelatedLink(related))
      },
      getInvoiceName(row) {
        return get(row, 'invoice.name', this.$t('generic.na'))
      },
      getRelatedLink (related) {
        if (!related) {
          return
        }
        let data = related.data
        if (!related.entity) {
          return ''
        }
        let path = `/${related.entity}s`
        if (data.uuid) {
          path += `/${data.uuid}`
        }
        return path
      },
      async approveOrder(row) {
        await OrderService.orderVerify(row.uuid, {status: orderStatuses.SUCCESS})
        row.status = orderStatuses.SUCCESS
      },
      async rejectOrder(row) {
        await OrderService.orderVerify(row.uuid, {status: orderStatuses.REJECTED})
        row.status = orderStatuses.REJECTED
      },
      refreshTable() {
        this.$refs.table.getTableData()
      },
      async exportOrders(query, all = false) {
        let data = cloneDeep(query)
        if (all) {
          data.sort.limit = 999
        }
        await OrderService.export(data, 'csv')
      },
      async exportOrdersXLSX(query, all = false) {
        let data = cloneDeep(query)
        if (all) {
          data.sort.limit = 999
        }
        await OrderService.export(data, 'xlsx')
      }
    }
  }
</script>
<style lang="scss">
  .order__detail-cell {
    line-height: 23px;
  }

  .order__row {
    min-height: 35px;
    padding: 10px 0;
    display: flex;
    align-items: center;
  }

  .order__header {
    border-bottom: 1px solid $medium-gray;
    min-height: 40px;
  }

</style>
